import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { FormContext } from '../FormContext';
import AMLFieldView from '../view/AMLFieldView';
import { FormPreviewContext } from '../../engine/FormPreviewEngine';
import fetchAPI from '../../../utils/fetchAPI';
import getApiEndpoints, { EndpointTypes } from '../FormEndpoints';

const AMLFieldEngine = ({ internalId, lookupInfo = {}, uiType }) => {
  const formContextProps = useContext(FormContext);
  const formPreviewContextProps = useContext(FormPreviewContext);

  const apiEndpoints = useMemo(
    () => getApiEndpoints(EndpointTypes.END_USER),
    []
  );

  const mappedFields =
    formContextProps.mappedFields || formPreviewContextProps.mappedFields;
  const showAdminFields = formContextProps.showAdminFields;
  const integrations = formContextProps.integrations;

  const initialUserSubmission = formContextProps.initialUserSubmission || null;
  const { formId, onFormChange, validateField } = formContextProps;

  const [integrationId, setIntegrationId] = useState(null);

  const [subFields, setSubFields] = useState([]);
  const [subFieldValues, setSubFieldValues] = useState([]);
  const [subFieldsDisabled, setSubFieldsDisabled] = useState([]);
  const [defaultThirdfortCheckTypes, setDefaultThirdfortCheckTypes] = useState(
    '||Enhanced NFC ID||'
  );

  const [hiddenSubFields, setHiddenSubFields] = useState([]);

  const lookupSubFields = lookupInfo.subFields;

  const [documentIdTypeOptions, setDocumentIdTypeOptions] = useState([]);
  const shouldPreloadDocumentIdTypesForCountry = useRef(false);

  useEffect(() => {
    let fields = [],
      matchingField = null,
      results = [],
      initialSubValues = [],
      fieldToPush = null,
      idCountryPreset = '';
    loadIntegrationData();
    if (mappedFields) {
      fields = cloneDeep(mappedFields);
    }
    lookupSubFields.forEach(lookup => {
      if (lookup && lookup.internalId) {
        fieldToPush = null;
        matchingField = Object.values(fields).find(one => {
          return !!(
            one &&
            one.lookupInfo &&
            one.lookupInfo.subFieldInfo &&
            one.lookupInfo.subFieldInfo.parentFieldId === internalId &&
            one.lookupInfo.subFieldInfo.externalFormFieldId ===
              lookup.internalId
          );
        });
        if (matchingField) {
          fieldToPush = cloneDeep(matchingField);
          fieldToPush.internalId = lookup.internalId;
        }
        if (fieldToPush) {
          results.push({
            ...fieldToPush,
            localFormFieldId: lookup.localFormFieldId
          });
          if (
            matchingField.internalId &&
            initialUserSubmission &&
            initialUserSubmission.endUserInput
          ) {
            if (
              typeof initialUserSubmission.endUserInput[
                matchingField.internalId
              ] !== 'undefined'
            ) {
              initialSubValues.push(
                initialUserSubmission.endUserInput[matchingField.internalId]
              );
              if (
                matchingField?.lookupInfo?.subFieldInfo?.externalFormFieldId ===
                'documentIdCountry'
              ) {
                idCountryPreset =
                  initialUserSubmission.endUserInput[matchingField.internalId];
              }
            } else if (
              initialUserSubmission.adminInput &&
              typeof initialUserSubmission.adminInput[
                matchingField.internalId
              ] !== 'undefined'
            ) {
              initialSubValues.push(
                initialUserSubmission.adminInput[matchingField.internalId]
              );
              if (
                matchingField?.lookupInfo?.subFieldInfo?.externalFormFieldId ===
                'documentIdCountry'
              ) {
                idCountryPreset =
                  initialUserSubmission.adminInput[matchingField.internalId];
              }
            } else {
              initialSubValues.push('');
            }
          }
        }
      }
    });
    if (idCountryPreset) {
      shouldPreloadDocumentIdTypesForCountry.current = idCountryPreset;
    }
    setSubFields(results);
    setSubFieldValues(initialSubValues);
  }, [lookupSubFields]);

  useEffect(() => {
    if (integrationId && shouldPreloadDocumentIdTypesForCountry.current) {
      loadDocumentIdTypes(shouldPreloadDocumentIdTypesForCountry.current);
    }
  }, [integrationId, shouldPreloadDocumentIdTypesForCountry]);

  const onSubFieldFocus = useCallback(
    index => {
      const fieldNode = document.querySelector(
        `[field-id="${subFields[index].localFormFieldId}"]`
      );
      if (!fieldNode) {
        return;
      }
      const errorP = fieldNode.querySelector('[data-error]');
      if (!errorP) {
        return;
      }
      fieldNode.classList.remove('EUF-error');
      errorP.textContent = '';
    },
    [subFields, subFieldValues]
  );

  const loadIntegrationData = () => {
    if (!integrations || !integrations.length) {
      return;
    }
    let activeIntegrationMatchingUIType = null;
    switch (uiType) {
      case 'thirdfort':
        activeIntegrationMatchingUIType = integrations.find(
          one => one.integrationType === 'thirdfort' // && one.active
        );
        break;
      case 'worldcheck':
      case 'worldcheck_org':
        activeIntegrationMatchingUIType = integrations.find(
          one =>
            (one.integrationType === 'worldcheck' ||
              one.integrationType === 'worldcheck_org') &&
            one.active
        );
        break;
      case 'credas':
      case 'credas_out_of_scope':
        activeIntegrationMatchingUIType = integrations.find(
          one =>
            one.integrationType === 'credas' ||
            one.integrationType === 'credas_out_of_scope'
          // missing && one.active
        );
        break;
      default:
        activeIntegrationMatchingUIType = integrations.find(one => one.active);
        break;
    }

    if (activeIntegrationMatchingUIType) {
      if (uiType === 'thirdfort') {
        // logic to check if ongoing is on
        const ongoingStartsOn = true;
        if (ongoingStartsOn) {
          setDefaultThirdfortCheckTypes(
            '||Enhanced NFC ID||Ongoing Monitoring||'
          );
        } else {
          setDefaultThirdfortCheckTypes('||Enhanced NFC ID||');
        }
        setIntegrationId(activeIntegrationMatchingUIType.id);
      } else {
        setIntegrationId(activeIntegrationMatchingUIType.id);
      }
    } else {
      return false;
    }
  };

  const onSubFieldBlur = useCallback(
    index => {
      validateField(subFields[index].localFormFieldId, subFieldValues[index]);
    },
    [subFields, subFieldValues]
  );

  const loadDocumentIdTypes = countryName => {
    if (!countryName || !integrationId) {
      setDocumentIdTypeOptions([]);
      return;
    }
    fetchAPI(
      `/scheduler/v1/wc/reference/integration/${integrationId}/identityDocumentLocation/${countryName}`
    ).then(res => {
      setDocumentIdTypeOptions(res.map(one => one.name));
    });
  };

  const setSubFieldValue = useCallback(
    (index, value) => {
      let newSubFieldValues = [...subFieldValues];
      newSubFieldValues[index] = value;
      if (subFields[index].internalId === 'documentIdCountry') {
        loadDocumentIdTypes(value);
        // clear document type
        subFields.forEach((one, j) => {
          if (one.internalId === 'documentIdType') {
            newSubFieldValues[j] = undefined;
          }
        });
      }
      if (subFields[index].uiType === 'multiple_choice' && !value) {
        newSubFieldValues[index] = '||||';
      }
      setSubFieldValues(newSubFieldValues);
      if (['credas', 'worldcheck', 'worldcheck_org'].includes(uiType)) {
        onFormChange(false, false, {
          [subFields[index].localFormFieldId]: value
        });
      } else {
        onFormChange();
      }
    },
    [integrationId, subFields, subFieldValues]
  );

  const validateDocFields = useCallback(() => {
    let indicesOfDocumentIdFields, foundEmpty, foundNotEmpty;
    indicesOfDocumentIdFields = [];
    foundEmpty = false;
    foundNotEmpty = false;
    subFields.forEach((field, index) => {
      if (
        ['documentId', 'documentIdType', 'documentIdCountry'].indexOf(
          field.internalId
        ) !== -1
      ) {
        indicesOfDocumentIdFields.push(index);
      }
    });
    if (indicesOfDocumentIdFields.length) {
      indicesOfDocumentIdFields.forEach(i => {
        if (subFieldValues[i]) {
          foundNotEmpty = true;
        } else {
          foundEmpty = true;
        }
      });
      if (foundNotEmpty && foundEmpty) {
        return false;
      }
    }
    return true;
  }, [subFields, subFieldValues]);

  const FIELDS_REQUIRED_FOR_CREDAS = [];

  const checkIfRequiredByFormLogic = useCallback(
    whichSubfield => {
      if (uiType !== 'credas') {
        return false;
      }
      const indexOfContactViaField = subFields.findIndex(
        one => one.internalId === 'contactVia'
      );
      const indexOfCheckTypeField = subFields.findIndex(
        one => one.internalId === 'checktype'
      );
      const checkTypeValue =
        indexOfCheckTypeField !== -1
          ? subFieldValues[indexOfCheckTypeField]
          : '';
      const requiredFieldsBasedOnCheckTypeValue =
        FIELDS_REQUIRED_FOR_CREDAS[checkTypeValue] || [];
      if (requiredFieldsBasedOnCheckTypeValue.includes(whichSubfield)) {
        return true;
      }
      if (indexOfContactViaField === -1) {
        return false;
      }
      const contactViaValue = subFieldValues[indexOfContactViaField];
      if (contactViaValue === whichSubfield) {
        return true;
      }
      return false;
    },
    [uiType, subFields, subFieldValues]
  );

  const viewProps = {
    defaultThirdfortCheckTypes,
    documentIdTypeOptions,
    hiddenSubFields,
    onSubFieldFocus,
    onSubFieldBlur,
    showAdminFields,
    subFields,
    subFieldValues,
    setSubFieldValue,
    uiType,
    validateDocFields,
    checkIfRequiredByFormLogic
  };

  return <AMLFieldView {...viewProps} />;
};

export default AMLFieldEngine;
